import { supabase } from './supabase';

// ─────────────────────────────────────────
// Shared loader for platform_settings.plan_access_matrix
//
// The same JSON blob is consumed by:
//   • AdminPlanAccessPage (admin edit UI)
//   • ServiceGate / usePlanAccess (route gating)
//   • ServiceLandingPage (Em-breve badge and disabled CTAs)
//
// Shape (backwards-compatible):
//   {
//     "_meta": { "coming_soon_catalog_slugs": ["astro-pet", ...] },
//     "birth-chart": { "free": {...}, "monthly": {...}, "annual": {...} },
//     ...
//   }
//
// Keys starting with "_" are metadata. All other keys are service IDs
// as used in AdminPlanAccessPage (different from services.ts slugs).
// ─────────────────────────────────────────

export type PlanTier = 'free' | 'monthly' | 'annual';

export interface ServiceAccess {
  enabled:          boolean;
  quota_per_month:  number | null;
  quota_per_year:   number | null; // null = unlimited; only meaningful for annual tier
}

export type ServiceRow = Record<PlanTier, ServiceAccess>;

export interface MatrixMeta {
  coming_soon_catalog_slugs?: string[]; // services.ts slugs (NOT matrix ids)
}

export type AccessMatrix = Record<string, ServiceRow> & { _meta?: MatrixMeta };

// In-memory cache, shared across the whole app for ~5 min.
let cache: { matrix: AccessMatrix; at: number } | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

export async function loadPlanAccessMatrix(force = false): Promise<AccessMatrix | null> {
  if (!force && cache && Date.now() - cache.at < CACHE_TTL_MS) return cache.matrix;

  const { data, error } = await supabase
    .from('platform_settings')
    .select('value')
    .eq('key', 'plan_access_matrix')
    .maybeSingle();

  if (error || !data?.value) return null;

  try {
    const parsed = typeof data.value === 'string' ? JSON.parse(data.value) : data.value;
    cache = { matrix: parsed as AccessMatrix, at: Date.now() };
    return cache.matrix;
  } catch {
    return null;
  }
}

export function clearPlanAccessMatrixCache() {
  cache = null;
}

/** Check whether the given services.ts slug is flagged as "coming soon". */
export function isComingSoon(matrix: AccessMatrix | null | undefined, catalogSlug: string): boolean {
  const list = matrix?._meta?.coming_soon_catalog_slugs;
  if (!Array.isArray(list)) return false;
  return list.includes(catalogSlug);
}
